// The fractal's colour lives here, as *data* — deliberately split from the escape
// maths in MandGen.js so we can audition looks without touching the numerics.
// The renderer hands us a position `t` in [0, 1] along the ramp (0 = escaped
// soonest / far exterior, 1 = escaped latest / hard against the set boundary)
// and we return an sRGB colour.
//
// Two rules the brief pins down:
//   - Interpolate in OKLab, not raw RGB — perceptually even, no muddy midpoints.
//   - Stay portable & deterministic: Phase 2's authoritative server render must
//     be able to reproduce this exact mapping, so keep it pure maths + constants.

// The default exterior ramp, as OKLCH stops: [t, L (0..1), C, H (deg)].
// A painterly cool→warm arc: near-ink void in the far exterior (low t), climbing
// to a luminous warm boundary (high t) where the set meets the exterior.
//
// Design notes from tuning against the whole set + a deep seahorse spiral:
//   - Cool half is kept vivid (a strong blue exterior is wanted), but the warm
//     half earns its colour through *hue travel* — deep copper in the shadowed
//     cores → orange → gold at the highlights — rather than one uniform sepia.
//   - A dusky-plum bridge sits at the cool→warm crossover. Straight OKLab
//     interpolation from teal to orange would pass through the low-chroma centre
//     (the old washed-out grey band); a muted plum keeps colour alive there.
//     It's deliberately LOW chroma and given a NARROW t-band: because histogram
//     equalisation hands equal screen area to equal t-width, a wide/saturated
//     bridge became a headline magenta ring — the plum is connective tissue, not
//     a feature colour.
//   - Peak luminance is held at ~0.88, not near-white, so the brights read as
//     rich gold instead of blown-out overexposure.
const RAMP = [
    [0.00, 0.15, 0.035, 260],  // near-ink cool — the void of the complex plane
    [0.22, 0.32, 0.100, 262],  // deep indigo
    [0.50, 0.52, 0.120, 210],  // vivid teal (holds the blue exterior longer)
    [0.60, 0.54, 0.085, 335],  // dusky-plum bridge — muted, narrow, connective
    [0.70, 0.58, 0.150,  30],  // deep copper/red — the shadowed warm cores
    [0.84, 0.74, 0.150,  60],  // orange
    [0.93, 0.85, 0.120,  82],  // gold
    [1.00, 0.88, 0.055,  88],  // rich warm highlight at the boundary (not white)
];

// Interior of the set (points that never escape): a deep *warm* near-black. It's
// dark enough that the big set body still reads as a quiet void, but its warmth
// means the tiny embedded islands scattered through the gold filigree read as a
// natural core-shadow of that gold rather than jarring cool "dead pixels". ~#120D09.
export const INTERIOR_RGB = [18, 13, 9];

const DEG = Math.PI / 180;

// Precompute each stop in OKLab (L, a, b). Interpolating in Lab rather than LCH
// sidesteps hue-wrap ambiguity and keeps midpoints clean.
const stopsLab = RAMP.map(([t, L, C, H]) => [
    t,
    L,
    C * Math.cos(H * DEG),
    C * Math.sin(H * DEG),
]);

// OKLab → sRGB byte, gamut-clamped. Björn Ottosson's matrices + the sRGB
// transfer function.
const linToByte = (c) => {
    const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(v * 255)));
};

const oklabToRgb = (L, a, b) => {
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;
    return [
        linToByte( 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
        linToByte(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
        linToByte(-0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s),
    ];
};

// Reference sampler: position t → [r, g, b]. Exact (rebuilds the interpolation
// each call) — used to bake the LUT below and handy for tests/tuning. The hot
// render path uses PALETTE_LUT instead.
export const paletteColor = (t) => {
    const x = t <= 0 ? 0 : t >= 1 ? 1 : t;
    let i = 1;
    while (i < stopsLab.length - 1 && x > stopsLab[i][0]) i++;
    const [t0, L0, a0, b0] = stopsLab[i - 1];
    const [t1, L1, a1, b1] = stopsLab[i];
    const f = t1 === t0 ? 0 : (x - t0) / (t1 - t0);
    return oklabToRgb(
        L0 + (L1 - L0) * f,
        a0 + (a1 - a0) * f,
        b0 + (b1 - b0) * f,
    );
};

// Baked lookup table for the render hot loop: a million per-pixel palette
// evaluations would otherwise redo the OKLab matrix (and three pow() calls)
// every time. Sample once into a flat RGB byte array; the renderer indexes it.
export const PALETTE_LUT_SIZE = 2048;
export const PALETTE_LUT = new Uint8ClampedArray(PALETTE_LUT_SIZE * 3);
for (let i = 0; i < PALETTE_LUT_SIZE; i++) {
    const [r, g, b] = paletteColor(i / (PALETTE_LUT_SIZE - 1));
    PALETTE_LUT[i * 3] = r;
    PALETTE_LUT[i * 3 + 1] = g;
    PALETTE_LUT[i * 3 + 2] = b;
}
